
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Dispatch } from "redux";
import { throttle } from 'lodash';
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";

import { RootState } from "../store";
import { debugData } from "../utils/debugData";
import { isEnvBrowser } from "../utils/misc";
import { useSocketEventOutsideRequest } from "../hooks/useSocketEventOutsideRequest";
import { useSocketEvent } from "../hooks/useSocketEvent";

import { socketRequest } from "../utils/socketRequest";

import { UserState, SET_PLAYERDATA } from "../reducers/userReducer";
import { PermissionType, PlayerType, HardMoneyState, SET_HOUSEDATA } from "../reducers/hardmoneyReducer";
import { UIState, SET_activeTab, SET_hidden, SET_isLoading } from "../reducers/uiReducer";

import HeaderPage from "./Header/HeaderPage";
import HomePage from "./Home/HomePage";
import UpgradesPage from "./Upgrades/UpgradesPage";
import LoadingSpinner from "./GlobalComponents/LoadingSpinner/LoadingSpinner";
import MaintenancePage from "./Maintenance/MaintenancePage";
import RentPage from "./Rent/RentPage";
import SettingsPage from "./Settings/SettingsPage";
import AccessPage from "./Access/AccessPage";
import AccountSettingsPage from "./User/Account/AccountSettingsPage";
import { RequireAuth } from "./User/Auth";

const LaptopUI: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const uiData = useSelector((state: RootState) => state.ui);
    const playerData = useSelector((state: RootState) => state.user);
    const hardmoneyData = useSelector((state: RootState) => state.hardmoney);

    const isInitialMount = useRef(true);

    // Sync URL with Redux state - URL is the source of truth on mount
    useEffect(() => {
        const path = location.pathname.replace('/', '') || 'home';
        if (path !== uiData.activeTab) {
            dispatch(SET_activeTab(path));
        }
    }, [location.pathname]);

    // Sync Redux state with URL when activeTab changes (but skip initial mount)
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const currentPath = location.pathname.replace('/', '') || 'home';
        if (uiData.activeTab !== currentPath) {
            navigate(`/${uiData.activeTab}`, { replace: true });
        }
    }, [uiData.activeTab]);

    const fetchHouseData = useCallback(
        throttle(async () => {
            dispatch(SET_isLoading(true));
            try {
                const res: HardMoneyState = await socketRequest("GetHouseData", {
                    houseOwner: hardmoneyData.houseOwner,
                    houseID: hardmoneyData.houseID,
                }, hardmoneyData);
                if (res) {
                    dispatch(SET_HOUSEDATA(res));
                } else {
                    throw new Error("Invalid house data received.");
                }
            } catch (err) {
                console.error("Error fetching house data:", err);
            } finally {
                dispatch(SET_isLoading(false));
            }
        }, 3000),
        [dispatch, hardmoneyData]
    );

    const isLoadedRef = useRef<boolean>(false);

    useEffect(() => {
        if (!isLoadedRef.current) {
            isLoadedRef.current = true;
            fetchHouseData();
        }
    }, []); 

    useSocketEvent("uiUpdate", (data) => {
        console.log("UI Update from any event:", data);
        // Triggers for BOTH:
        // 1. Response to socketRequest("getUIData")
        // 2. Unsolicited server update
    });

    useSocketEventOutsideRequest("uiUpdate", (data) => {
        console.log("UI Update outside request:", data);
        // Triggers for BOTH:
        // 1. Response to socketRequest("getUIData")
        // 2. Unsolicited server update
    });

    return (
        <div
            className="flexCenter"
            style={{
                width: "100%",
                minHeight: "100vh",
                boxSizing: "border-box",
                position: "relative",
            }}
        >
            <div
                style={{
                    width: "100%",
                    minHeight: "100vh",
                    backgroundColor: "rgba(20, 20, 20, 0.98)",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    padding: "2vh 4vh",
                    borderRadius: "0.5vh",
                    gap: "1vh",
                }}
            >
                <div
                    style={{
                        width: "100%",
                        height: "auto",
                        //backgroundColor: "red",
                        boxSizing: "border-box",
                    }}
                >
                    <HeaderPage/>
                </div>

                {uiData.isLoading ? (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "100%",
                            height: "100%",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "25%",
                                height: "25%",
                            }}
                        >
                            <LoadingSpinner
                                loadingName={"Loading..."}
                            />
                        </div>
                    </div>
                ): (
                    <div
                        style={{
                            width: "100%",
                            flex: 1,
                            boxSizing: "border-box",
                            overflowY: "auto",
                        }}
                    >
                        <Routes>
                            <Route path="/" element={
                                <Navigate to="/home" replace />
                            } />

                            <Route path="/home" element={
                                <HomePage />
                            } />
                            
                            <Route path="/upgrades" element={
                                <UpgradesPage />
                            } />

                            {/* Protected route - requires login */}
                            <Route path="/maintenance" element={
                                <RequireAuth>
                                    <MaintenancePage />
                                </RequireAuth>
                            } />

                            <Route path="/access" element={
                                <AccessPage />
                            } />

                            <Route path="/rent" element={
                                <RentPage />
                            } />

                            {/* Protected route - requires login */}
                            <Route path="/settings" element={
                                <RequireAuth>
                                    <SettingsPage />
                                </RequireAuth>
                            } />

                            {/* Protected route - requires login */}
                            <Route path="/account" element={
                                <RequireAuth>
                                    <AccountSettingsPage />
                                </RequireAuth>
                            } />
                        </Routes>
                    </div>
                )}

            </div>
        </div>
    );
};

export default LaptopUI;
